/** The town screen's dialogs: build, recruit, caravan, guild, shop, tavern,
 * and the three garrison windows.
 *
 * Split out of `TownScreen.tsx` on 2026-09-17 unchanged. Each takes the slice
 * of props it needs and a `Reason`-returning probe the host got from the
 * kernel; none of them decides anything (I10).
 */

import { useMemo, useState } from "react";
import type {
  ArmyId, ArmyState, ArtifactDefId, BuildingDefId, ContentRegistry, CreatureDef, CreatureDefId,
  GameCommand, HeroDefId, HeroState, ObjectId, Reason, Resources, TownId, TownState, UpgradeOffer,
} from "@game/core";
import { artifactIcon, buildingIcon, creatureIcon, heroPortrait, spellIcon } from "../art.js";
import { affordableCount, groupDigits, scaleCost, titleCase } from "../format.js";
import {
  CostRow, CountSlider, ICON_SMALL, PORTRAIT_SMALL, ART_ICON_LARGE,
  ReasonLine, costWords,
} from "../parts.js";
import { MaybeIcon, ScreenDialog, StatLine } from "../primitives.js";
import {
  buildOption, explainReason, guildView, recruitOption, recruitableCreatures, shopOption, shopStock,
} from "../townModel.js";
import type { ResolvedSlot } from "../townSlots.js";
import { armyDropCommand } from "../heroModel.js";
import type { ArmyDrag } from "../heroModel.js";

/**
 * One line of the caravan dialog. Mirrors the host's own `CaravanOffer`, and is
 * declared here rather than imported so `packages/ui` keeps depending on
 * nothing but the kernel's types.
 */
export interface TownCaravanOffer {
  readonly key: string;
  readonly from: string;
  readonly creature: CreatureDefId;
  readonly max: number;
  readonly stock: number;
  readonly days: number;
  readonly fromTown: TownId | null;
  readonly fromObject: ObjectId | null;
}

/** A column already walking. Mirrors the host's `CaravanInFlight`. */
export interface TownCaravanInFlight {
  readonly id: string;
  readonly creature: CreatureDefId;
  readonly count: number;
  readonly to: string;
  readonly daysLeft: number;
}

/** The kernel's price for a hero, mirrored so the tavern can show it. */
const HERO_HIRE_COST = 2500;

/** One socket, addressed the way the kernel addresses it. */
export interface SlotRef {
  readonly army: string;
  readonly slot: number;
}

// -------------------------------------------------------- the build list

/**
 * Everything the town could raise next, in one list.
 *
 * Without it nothing on the screen says the buildings are clickable, and a
 * player with 41,250 gold has no visible place to spend it. Legality is still
 * the kernel's: each row carries the `Reason` the host handed back.
 */
export function BuildListDialog(props: {
  readonly content: ContentRegistry;
  readonly slots: readonly ResolvedSlot[];
  readonly treasury: Resources;
  readonly buildReason: (b: BuildingDefId) => Reason | null;
  readonly onPick: (b: BuildingDefId) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const rows = props.slots
    .filter((r) => r.next !== null)
    .map((r) => ({ slot: r.slot, def: r.next!, reason: props.buildReason(r.next!.id) }))
    .sort((a, b) => (a.reason === null ? 0 : 1) - (b.reason === null ? 0 : 1)
      || a.def.name.localeCompare(b.def.name));

  return (
    <ScreenDialog title="Build" onClose={props.onClose} width={680}>
      {rows.length === 0 ? (
        <div className="hs-card hs-stone hs-tiny hs-muted">
          This town is finished. There is nothing left to raise.
        </div>
      ) : rows.map((row) => (
        <button
          key={row.def.id as string}
          type="button"
          className={`hs-buildrow${row.reason === null ? "" : " hs-buildrow--blocked"}`}
          onClick={() => props.onPick(row.def.id)}
          title={row.def.description}
        >
          <MaybeIcon atlasKey={buildingIcon(row.def.art)} size={56}
            className="hs-buildrow__art" title={row.def.name} />
          <span className="hs-buildrow__body">
            <span className="hs-buildrow__name">{row.def.name}</span>
            <span className="hs-tiny hs-muted hs-clip">{row.def.description}</span>
            <CostRow cost={row.def.cost} have={props.treasury} />
          </span>
          <ReasonLine content={props.content} reason={row.reason} />
        </button>
      ))}
    </ScreenDialog>
  );
}

// ------------------------------------------------------------ build dialog

export function BuildDialog(props: {
  readonly content: ContentRegistry;
  readonly town: TownState;
  readonly treasury: Resources;
  readonly building: BuildingDefId;
  readonly reason: Reason | null;
  readonly onBuild: (b: BuildingDefId) => void;
  readonly onClose: () => void;
}): JSX.Element | null {
  const def = props.content.buildings.get(props.building);
  if (def === undefined) return null;
  const option = buildOption(props.content, props.town, props.treasury, def, props.reason);
  const legal = props.reason === null;

  return (
    <ScreenDialog
      title={def.name}
      onClose={props.onClose}
      width={560}
      footer={
        <button
          type="button"
          className="hs-btn hs-btn--gold"
          disabled={!legal}
          title={legal ? "Build" : "See below"}
          onClick={() => props.onBuild(def.id)}
        >
          Build
        </button>
      }
    >
      <div className="hs-card hs-stone" style={{ marginBottom: "8px" }}>
        <div className="hs-card__title">Cost</div>
        <CostRow cost={def.cost} have={props.treasury} />
      </div>

      {option.prerequisites.length === 0 ? null : (
        <div className="hs-card hs-stone" style={{ marginBottom: "8px" }}>
          <div className="hs-card__title">Requires</div>
          {option.prerequisites.map((p) => (
            <div key={p.id as string} className={`hs-req ${p.met ? "hs-req--met" : "hs-req--unmet"}`}>
              <span className="hs-req__tick">{p.met ? "+" : "-"}</span>
              <span className="hs-clip">{p.building?.name ?? titleCase(p.id as string)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="hs-card hs-stone">
        <div className="hs-card__title">Effect</div>
        <div className="hs-tiny" style={{ lineHeight: 1.45 }}>{def.description}</div>
      </div>

      <ReasonLine content={props.content} reason={props.reason} />
    </ScreenDialog>
  );
}

// ---------------------------------------------------------- recruit dialog

export function RecruitDialog(props: {
  readonly content: ContentRegistry;
  readonly town: TownState;
  readonly treasury: Resources;
  readonly army: ArmyState | null;
  /** The kernel's count for each form, see `TownScreenProps.available`. */
  readonly available: (c: CreatureDefId) => number;
  readonly creature: CreatureDefId;
  readonly reasonFor?: ((c: CreatureDefId, count: number) => Reason | null) | undefined;
  readonly onRecruit: (c: CreatureDefId, n: number) => void;
  readonly onClose: () => void;
}): JSX.Element | null {
  const forms = useMemo(
    () => recruitableCreatures(props.content, props.town),
    [props.content, props.town],
  );
  const initial = props.content.creatures.get(props.creature) ?? forms[0] ?? null;
  const [pick, setPick] = useState<CreatureDef | null>(initial);
  const chosen = pick ?? initial;
  const option = chosen === null
    ? null
    : recruitOption(props.treasury, props.army, chosen, props.available(chosen.id));
  const [count, setCount] = useState<number>(option?.maxCount ?? 0);

  if (chosen === null || option === null) return null;
  const max = option.maxCount;
  const n = count > max ? max : count;
  const total = scaleCost(chosen.cost, n);
  const reason = props.reasonFor?.(chosen.id, n) ?? null;
  const legal = n > 0 && reason === null;

  return (
    <ScreenDialog
      title={`Recruit ${chosen.namePlural}`}
      onClose={props.onClose}
      width={620}
      footer={
        <button type="button" className="hs-btn hs-btn--gold" disabled={!legal}
          onClick={() => props.onRecruit(chosen.id, n)}>
          {`Recruit ${String(n)}`}
        </button>
      }
    >
      <div style={{ display: "flex", gap: "12px", marginBottom: "10px" }}>
        <div className="hs-portrait">
          <MaybeIcon atlasKey={creatureIcon(chosen)} size={168} title={chosen.name} />
        </div>
        <div className="hs-card hs-stone" style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="hs-card__title">{chosen.name}</div>
          <div className="hs-tiny hs-muted" style={{ marginBottom: "6px" }}>
            {`Tier ${String(chosen.tier)}${chosen.upgraded ? " (upgraded)" : ""} · `}
            {`Attack ${String(chosen.attack)} · Defense ${String(chosen.defense)} · `}
            {`Damage ${String(chosen.damageMin)}-${String(chosen.damageMax)} · `}
            {`HP ${String(chosen.hp)} · Speed ${String(chosen.speed)}`}
          </div>
          <StatLine label="Available" value={groupDigits(option.available)} />
          <StatLine label="Each" value={<CostRow cost={chosen.cost} have={props.treasury} />} />
        </div>
      </div>

      {forms.length < 2 ? null : (
        <div className="hs-choices" style={{ marginBottom: "10px" }}>
          {forms.map((f) => (
            <button
              key={f.id as string}
              type="button"
              className={`hs-choice ${f.id === chosen.id ? "hs-choice--on" : ""}`}
              onClick={() => { setPick(f); setCount(0); }}
            >
              {f.name}
              <span className="hs-choice__sub">
                {groupDigits(props.available(f.id))}
              </span>
            </button>
          ))}
        </div>
      )}

      <CountSlider value={n} max={max} label="Number to recruit" onChange={setCount} />

      <div className="hs-card hs-stone" style={{ marginTop: "10px" }}>
        <div className="hs-card__title">Total</div>
        <CostRow cost={total} have={props.treasury} />
      </div>

      <ReasonLine content={props.content} reason={reason} lead="Cannot recruit:" />
    </ScreenDialog>
  );
}

// ---------------------------------------------------------- caravan dialog

/**
 * Reinforce this town from somewhere else.
 *
 * The dialog is framed as "what can reach here" rather than as "where can this
 * town send", because that is the decision a player is actually making: they
 * are standing in the hall that needs troops. It is also the framing that lets
 * one dialog cover both sources - another town's pool and an owned external
 * dwelling's stock - since from here they are the same thing, a place with
 * creatures in it and a number of days away.
 *
 * Every line comes from the host, which got it from the kernel. Nothing here
 * decides what may be sent or how long it takes.
 */
export function CaravanDialog(props: {
  readonly content: ContentRegistry;
  readonly town: TownState;
  readonly treasury: Resources;
  readonly offers: readonly TownCaravanOffer[];
  readonly inFlight: readonly TownCaravanInFlight[];
  readonly onSend: (offer: TownCaravanOffer, count: number) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const [pickKey, setPickKey] = useState<string>(props.offers[0]?.key ?? "");
  const chosen = props.offers.find((o) => o.key === pickKey) ?? props.offers[0] ?? null;
  const [count, setCount] = useState<number>(chosen?.max ?? 0);
  const max = chosen?.max ?? 0;
  const n = count > max ? max : count;
  const def = chosen === null ? undefined : props.content.creatures.get(chosen.creature);
  const total = def === undefined ? {} : scaleCost(def.cost, n);

  return (
    <ScreenDialog
      title={`Caravans to ${props.town.name}`}
      onClose={props.onClose}
      width={620}
      footer={
        <button
          type="button" className="hs-btn hs-btn--gold"
          disabled={chosen === null || n <= 0}
          onClick={() => { if (chosen !== null && n > 0) props.onSend(chosen, n); }}
        >
          {chosen === null ? "Send" : `Send ${String(n)} — ${String(chosen.days)}d`}
        </button>
      }
    >
      {props.inFlight.length === 0 ? null : (
        <div className="hs-card hs-stone" style={{ marginBottom: "10px" }}>
          <div className="hs-card__title">On the road</div>
          {props.inFlight.map((cv) => {
            const cd = props.content.creatures.get(cv.creature);
            const what = cv.count === 1
              ? cd?.name ?? (cv.creature as string)
              : cd?.namePlural ?? (cv.creature as string);
            return (
              <StatLine
                key={cv.id}
                label={`${groupDigits(cv.count)} ${what} → ${cv.to}`}
                value={cv.daysLeft <= 0 ? "arriving" : `${String(cv.daysLeft)}d`}
              />
            );
          })}
        </div>
      )}

      {props.offers.length === 0 ? (
        <div className="hs-tiny hs-muted">
          Nowhere else you hold has creatures to send.
        </div>
      ) : (
        <>
          <div className="hs-choices" style={{ marginBottom: "10px" }}>
            {props.offers.map((o) => {
              const cd = props.content.creatures.get(o.creature);
              return (
                <button
                  key={o.key}
                  type="button"
                  className={`hs-choice ${o.key === chosen?.key ? "hs-choice--on" : ""}`}
                  onClick={() => { setPickKey(o.key); setCount(o.max); }}
                >
                  {`${cd?.namePlural ?? (o.creature as string)} · ${o.from}`}
                  <span className="hs-choice__sub">
                    {`${groupDigits(o.stock)} · ${String(o.days)} days`}
                  </span>
                </button>
              );
            })}
          </div>

          {chosen === null ? null : (
            <>
              <CountSlider value={n} max={max} label="Number to send" onChange={setCount} />
              <div className="hs-card hs-stone" style={{ marginTop: "10px" }}>
                <div className="hs-card__title">Total</div>
                <CostRow cost={total} have={props.treasury} />
                <div className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>
                  {`Paid now. They march into the garrison in ${String(chosen.days)} `}
                  {chosen.days === 1 ? "day" : "days"}
                  {". If the town is not yours when they arrive, they are lost."}
                </div>
              </div>
            </>
          )}
        </>
      )}
    </ScreenDialog>
  );
}

// ------------------------------------------------------------ guild dialog

export function GuildDialog(props: {
  readonly content: ContentRegistry;
  readonly town: TownState;
  readonly onClose: () => void;
}): JSX.Element {
  const levels = guildView(props.content, props.town);
  return (
    <ScreenDialog title="Mage Guild" onClose={props.onClose} width={660}>
      {levels.map((lvl) => (
        <div className="hs-card hs-stone" key={lvl.level} style={{ marginBottom: "8px" }}>
          <div className="hs-card__title">
            {`Level ${String(lvl.level)}${lvl.built ? "" : " — not built"}`}
          </div>
          {!lvl.built ? (
            <div className="hs-tiny hs-muted">This shelf is still empty stone.</div>
          ) : lvl.spells.length === 0 ? (
            <div className="hs-tiny hs-muted">No spells were bound to this level.</div>
          ) : (
            <div className="hs-grid">
              {lvl.spells.map((s) => (
                <div className="hs-skill" key={s.id as string} title={s.description}>
                  <MaybeIcon atlasKey={spellIcon(s)} size={ART_ICON_LARGE} title={s.name} />
                  <span className="hs-skill__text">
                    <span className="hs-skill__name">{s.name}</span>
                    <span className="hs-skill__level">{`${s.school} · ${String(s.cost[0])} mana`}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </ScreenDialog>
  );
}

// ----------------------------------------------------------- tavern dialog

/**
 * The Blacksmith counter.
 *
 * A war machine bought here goes straight onto the hero rather than into his
 * backpack - that is the kernel's rule and it is why the counter is worth a
 * window at all: one click and the ballista is on the field. The window
 * therefore refuses to sell to a town with nobody standing in it, and says so,
 * because "buy" with no hero would be a button that does nothing.
 */
export function ShopDialog(props: {
  readonly content: ContentRegistry;
  readonly town: TownState;
  readonly treasury: Resources;
  readonly hero: HeroState | null;
  readonly checkCommand?: ((cmd: GameCommand) => Reason | null) | undefined;
  readonly onBuy: (a: ArtifactDefId) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const { content, town, hero } = props;
  const stock = useMemo(() => shopStock(content, town), [content, town]);
  const options = useMemo(() => stock.map((a) => shopOption(
    content, a, props.treasury, hero,
    hero === null || props.checkCommand === undefined ? null : props.checkCommand({
      type: "TOWN_BUY_ARTIFACT", hero: hero.id, town: town.id, artifact: a,
    }),
  )), [content, stock, props.treasury, hero, props.checkCommand, town.id]);

  return (
    <ScreenDialog title="Blacksmith" onClose={props.onClose} width={620}>
      <div className="hs-tiny hs-muted" style={{ marginBottom: "8px" }}>
        {hero === null
          ? "Nobody is standing in this town. A machine bought here is fitted to a hero, so bring one in first."
          : `Fitted to ${content.heroes.get(hero.def)?.name ?? "the visiting hero"} at once`
            + " - a war machine in the backpack is a war machine that stays at home."}
      </div>
      {options.length === 0 ? (
        <div className="hs-card hs-stone hs-tiny hs-muted">
          This town builds nothing that sells over a counter.
        </div>
      ) : (
        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {options.map((o) => {
            const legal = hero !== null && o.reason === null;
            return (
              <div
                className="hs-card hs-stone"
                key={o.artifact as string}
                style={{ flex: "1 1 180px", minWidth: 0 }}
              >
                <div className="hs-portrait" style={{ marginBottom: "6px" }}>
                  <MaybeIcon
                    atlasKey={o.def === null ? null : artifactIcon(o.def)}
                    size={ART_ICON_LARGE}
                    title={o.def?.name ?? "Artifact"}
                  />
                </div>
                <div className="hs-hero__name" style={{ fontSize: "max(14px, var(--hm-type-floor))" }}>
                  {o.def?.name ?? titleCase(o.artifact as string)}
                </div>
                <div className="hs-tiny hs-muted" style={{ minHeight: "2.4em" }}>
                  {o.def?.description ?? ""}
                </div>
                <div className="hs-cost" style={{ justifyContent: "center", margin: "6px 0" }}>
                  <CostRow cost={o.cost} have={props.treasury} />
                </div>
                <button
                  type="button"
                  className="hs-btn hs-btn--gold"
                  disabled={!legal}
                  style={{ width: "100%" }}
                  onClick={() => { props.onBuy(o.artifact); }}
                >
                  {o.owned ? "Carried" : "Buy"}
                </button>
                <ReasonLine content={content} reason={o.reason} lead="Cannot buy:" />
              </div>
            );
          })}
        </div>
      )}
    </ScreenDialog>
  );
}

export function TavernDialog(props: {
  readonly content: ContentRegistry;
  readonly offers: readonly HeroDefId[];
  readonly treasury: Resources;
  readonly reasonFor?: ((h: HeroDefId) => Reason | null) | undefined;
  readonly onHire: (h: HeroDefId) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const affordable = affordableCount(props.treasury, { gold: HERO_HIRE_COST }, 1) > 0;
  return (
    <ScreenDialog title="Tavern" onClose={props.onClose} width={640}>
      <div className="hs-tiny hs-muted" style={{ marginBottom: "8px" }}>
        {`A hero costs ${groupDigits(HERO_HIRE_COST)} gold and arrives with the army the road gave them.`}
      </div>
      {props.offers.length === 0 ? (
        <div className="hs-card hs-stone hs-tiny hs-muted">
          Nobody worth hiring is drinking here this week.
        </div>
      ) : (
        <div style={{ display: "flex", gap: "10px" }}>
          {props.offers.map((id) => {
            const def = props.content.heroes.get(id);
            const reason = props.reasonFor?.(id) ?? null;
            const legal = reason === null && affordable;
            return (
              <div className="hs-card hs-stone" key={id as string} style={{ flex: "1 1 0", minWidth: 0 }}>
                <div className="hs-portrait" style={{ marginBottom: "6px" }}>
                  <MaybeIcon
                    atlasKey={def === undefined ? null : heroPortrait(def.portrait)}
                    size={PORTRAIT_SMALL * 2}
                    title={def?.name ?? "Hero"}
                  />
                </div>
                <div className="hs-hero__name" style={{ fontSize: "max(15px, var(--hm-type-floor))" }}>{def?.name ?? titleCase(id as string)}</div>
                <div className="hs-hero__class">{def?.heroClass ?? ""}</div>
                <div className="hs-cost" style={{ justifyContent: "center", margin: "6px 0" }}>
                  <CostRow cost={{ gold: HERO_HIRE_COST }} have={props.treasury} />
                </div>
                <button type="button" className="hs-btn hs-btn--gold" disabled={!legal}
                  style={{ width: "100%" }} onClick={() => props.onHire(id)}>
                  Hire
                </button>
                <ReasonLine content={props.content} reason={reason} lead="Cannot hire:" />
              </div>
            );
          })}
        </div>
      )}
      <div className="hs-tiny hs-muted" style={{ marginTop: "8px" }}>
        <MaybeIcon atlasKey="ui.resource.res_gold_ui" size={ICON_SMALL} title="gold" />
      </div>
    </ScreenDialog>
  );
}

// ------------------------------------------------------- garrison dialogs
//
// Three windows and one rule between them: the command is built by
// `armyDropCommand` (the same function the hero sheet drags through), the
// verdict comes from `checkCommand`, which is the kernel, and the confirm
// button is disabled with the kernel's own sentence under it. None of the
// three decides anything.

function slotOf(
  armies: ReadonlyMap<string, ArmyState>, at: SlotRef,
): { readonly army: ArmyState; readonly stack: NonNullable<ArmyState["slots"][number]> } | null {
  const army = armies.get(at.army);
  if (army === undefined) return null;
  const stack = army.slots[at.slot] ?? null;
  if (stack === null) return null;
  return { army, stack };
}

/** The first free socket in an army, which is where a Split button puts things. */
function freeSlotIn(army: ArmyState | null): number {
  if (army === null) return -1;
  for (let i = 0; i < army.slots.length; i++) if ((army.slots[i] ?? null) === null) return i;
  return -1;
}

/**
 * How many creatures a split may move.
 *
 * Inside one army the source has to keep at least one - an army slot is either
 * a stack or empty, and moving all of them is a swap, not a split. Across two
 * armies the whole stack may go, and it is the kernel that decides whether the
 * hero is left with nothing (LAST_STACK), not this dialog.
 */
function splitCeiling(from: SlotRef, to: SlotRef, whole: number): number {
  return from.army === to.army ? Math.max(1, whole - 1) : whole;
}

export function SplitDialog(props: {
  readonly content: ContentRegistry;
  readonly armies: ReadonlyMap<string, ArmyState>;
  readonly from: SlotRef;
  readonly to: SlotRef;
  readonly checkCommand?: ((cmd: GameCommand) => Reason | null) | undefined;
  readonly onSplit: (cmd: GameCommand) => void;
  readonly onClose: () => void;
}): JSX.Element | null {
  const src = slotOf(props.armies, props.from);
  const dstArmy = props.armies.get(props.to.army) ?? null;
  const whole = src?.stack.count ?? 0;
  const max = splitCeiling(props.from, props.to, whole);
  const [count, setCount] = useState(() => Math.max(1, Math.floor(whole / 2)));

  if (src === null || dstArmy === null) return null;
  const def = props.content.creatures.get(src.stack.def) ?? null;
  const n = count < 1 ? 1 : count > max ? max : count;

  const drag: ArmyDrag = {
    fromArmy: src.army, fromSlot: props.from.slot,
    toArmy: dstArmy, toSlot: props.to.slot,
    single: false, count: n,
  };
  const cmd = armyDropCommand(drag);
  const reason = cmd === null ? null : props.checkCommand?.(cmd) ?? null;
  const legal = cmd !== null && reason === null;

  return (
    <ScreenDialog
      title={`Split ${def?.namePlural ?? "stack"}`}
      onClose={props.onClose}
      width={520}
      footer={
        <button type="button" className="hs-btn hs-btn--gold" disabled={!legal}
          title={legal ? "Move them" : "See below"}
          onClick={() => { if (cmd !== null) props.onSplit(cmd); }}>
          {`Move ${groupDigits(n)}`}
        </button>
      }
    >
      <div style={{ display: "flex", gap: "12px", marginBottom: "10px", alignItems: "center" }}>
        <div className="hs-portrait">
          <MaybeIcon atlasKey={def === null ? null : creatureIcon(def)} size={ART_ICON_LARGE * 2}
            title={def?.name ?? ""} />
        </div>
        <div className="hs-card hs-stone" style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="hs-card__title">{def?.name ?? "Stack"}</div>
          <StatLine label="In this stack" value={groupDigits(whole)} />
          <StatLine label="Staying" value={groupDigits(whole - n)} />
          <StatLine label="Moving" value={groupDigits(n)} />
        </div>
      </div>

      <CountSlider value={n} max={max} label="Creatures to move" onChange={setCount} />

      <ReasonLine content={props.content} reason={reason} lead="Cannot move:" />
    </ScreenDialog>
  );
}

/** One stack, clicked: what it is, and the two things a player can do to it. */
export function StackDialog(props: {
  readonly content: ContentRegistry;
  readonly armies: ReadonlyMap<string, ArmyState>;
  readonly at: SlotRef;
  readonly town: TownId;
  readonly treasury: Resources;
  readonly garrison: ArmyState | null;
  readonly checkCommand?: ((cmd: GameCommand) => Reason | null) | undefined;
  /** The kernel's quotes for an army here, see `TownScreenProps.upgradeOffers`. */
  readonly upgradeOffers: (army: ArmyId) => readonly UpgradeOffer[];
  readonly onSplit: (from: SlotRef, to: SlotRef) => void;
  readonly onDismiss: (at: SlotRef) => void;
  readonly onUpgrade: (cmd: GameCommand) => void;
  readonly onClose: () => void;
}): JSX.Element | null {
  const here = slotOf(props.armies, props.at);
  if (here === null) return null;
  const def = props.content.creatures.get(here.stack.def) ?? null;
  const free = freeSlotIn(here.army);
  const canSplit = free >= 0 && here.stack.count > 1;
  const dismissReason = props.checkCommand?.({
    type: "DISMISS_STACK", army: here.army.id, slot: props.at.slot,
  }) ?? null;

  // The upgrade this town offers for this stack, if any, quoted by the kernel:
  // the same `townUpgradeOffers` its UPGRADE_UNITS reads, so the target and
  // the bill here are the ones the command will use. No offer, no button. A
  // town upgrades only into what its own dwellings produce, so a Spearwarden
  // stack in a town with no Glaivewarden dwelling is not an upgrade the player
  // is being refused, it is one the town does not sell; what can still be
  // refused with the button showing - the purse - keeps its reason line.
  //
  // `def` is checked first for the reason it always was: a save from an older
  // content bundle, or a hand-authored map, can name a creature the registry
  // no longer has, and the dialog has to survive that rather than take the
  // whole town screen down with it.
  const offer = def === null
    ? null
    : props.upgradeOffers(here.army.id).find((o) => o.slot === props.at.slot) ?? null;
  const upTo = offer === null ? null : offer.to;
  const upCost = offer === null ? null : offer.cost;
  const upCmd: GameCommand | null = upTo === null ? null : {
    type: "UPGRADE_UNITS", town: props.town, army: here.army.id, slot: props.at.slot,
  };
  const upReason = upCmd === null ? null : props.checkCommand?.(upCmd) ?? null;

  return (
    <ScreenDialog
      title={`${groupDigits(here.stack.count)} ${def?.namePlural ?? "creatures"}`}
      onClose={props.onClose}
      width={520}
      footer={(
        <>
          <button type="button" className="hs-btn" disabled={!canSplit}
            title={canSplit ? "Split this stack in two" : "There is no free socket in this army"}
            onClick={() => { props.onSplit(props.at, { army: props.at.army, slot: free }); }}>
            Split
          </button>
          <button type="button" className="hs-btn" disabled={dismissReason !== null}
            title={dismissReason === null
              ? "Disband these creatures"
              : explainReason(props.content, dismissReason)}
            onClick={() => { props.onDismiss(props.at); }}>
            Dismiss
          </button>
          {upTo === null ? null : (
            <button type="button" className="hs-btn hs-btn--gold" disabled={upReason !== null}
              title={upReason === null
                ? `Pay ${costWords(upCost)}`
                : explainReason(props.content, upReason)}
              onClick={() => { if (upCmd !== null) props.onUpgrade(upCmd); }}>
              {`Upgrade to ${upTo.name}`}
            </button>
          )}
        </>
      )}
    >
      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
        <div className="hs-portrait">
          <MaybeIcon atlasKey={def === null ? null : creatureIcon(def)} size={ART_ICON_LARGE * 2}
            title={def?.name ?? ""} />
        </div>
        <div className="hs-card hs-stone" style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="hs-card__title">{def?.name ?? "Stack"}</div>
          {def === null ? null : (
            <div className="hs-tiny hs-muted" style={{ marginBottom: "6px" }}>
              {`Attack ${String(def.attack)} · Defense ${String(def.defense)} · `}
              {`Damage ${String(def.damageMin)}-${String(def.damageMax)} · `}
              {`HP ${String(def.hp)} · Speed ${String(def.speed)}`}
            </div>
          )}
          <StatLine label="Count" value={groupDigits(here.stack.count)} />
        </div>
      </div>
      <div className="hs-tiny hs-muted" style={{ marginTop: "8px", lineHeight: 1.45 }}>
        Drag a stack onto the other army to move it; hold Shift while dragging to
        move part of it.
      </div>
      {upTo === null ? null : (
        <div className="hs-card hs-stone" style={{ marginTop: "8px" }}>
          <div className="hs-card__title">{`Upgrade to ${upTo.namePlural}`}</div>
          {/* Every resource whose cost rises, not just gold.
              `upgradePrice` returns the whole difference, and thirteen of the
              eighty-six upgrades in this content set take a second resource.
              Measured rather than assumed: those thirteen are ALL of tier 7 and
              nothing else - High Radiant, Umbral Wyrm, Everflame, Thunder Colossus, Ancient
              Great Maw, one per faction. So quoting the gold alone did not
              mislead a player one time in seven, it misled him exactly on the
              upgrade he spends a game saving for: the card said 2,000 gold, the
              kernel wanted two gems as well, and the button he had been walking
              toward was refused with no way to see why. */}
          <div className="hs-cost" style={{ margin: "2px 0 4px" }}>
            <CostRow cost={upCost ?? {}} have={props.treasury} />
          </div>
          <div className="hs-tiny hs-muted" style={{ marginTop: "4px", lineHeight: 1.45 }}>
            The difference in price, for all {groupDigits(here.stack.count)} of them.
            The stack keeps its number; no weekly growth is spent.
          </div>
          <ReasonLine content={props.content} reason={upReason} lead="Cannot upgrade:" />
        </div>
      )}
      <ReasonLine content={props.content} reason={dismissReason} lead="Cannot dismiss:" />
    </ScreenDialog>
  );
}

/**
 * Dismissing troops destroys them, so it asks. There is no undo and no refund;
 * the creatures do not go back into the town's weekly pool.
 */
export function DismissStackDialog(props: {
  readonly content: ContentRegistry;
  readonly armies: ReadonlyMap<string, ArmyState>;
  readonly at: SlotRef;
  readonly checkCommand?: ((cmd: GameCommand) => Reason | null) | undefined;
  readonly onDismiss: (cmd: GameCommand) => void;
  readonly onClose: () => void;
}): JSX.Element | null {
  const here = slotOf(props.armies, props.at);
  if (here === null) return null;
  const def = props.content.creatures.get(here.stack.def) ?? null;
  const cmd: GameCommand = { type: "DISMISS_STACK", army: here.army.id, slot: props.at.slot };
  const reason = props.checkCommand?.(cmd) ?? null;

  return (
    <ScreenDialog
      title="Dismiss these troops?"
      onClose={props.onClose}
      width={480}
      footer={
        <button type="button" className="hs-btn hs-btn--gold" disabled={reason !== null}
          title={reason === null ? "Disband them" : explainReason(props.content, reason)}
          onClick={() => { props.onDismiss(cmd); }}>
          Dismiss them
        </button>
      }
    >
      <div className="hs-card hs-stone">
        <div className="hs-card__title">
          {`${groupDigits(here.stack.count)} ${def?.namePlural ?? "creatures"}`}
        </div>
        <div className="hs-tiny" style={{ lineHeight: 1.45 }}>
          They are disbanded on the spot. Nothing is refunded and they do not
          return to the town&apos;s weekly pool.
        </div>
      </div>
      <ReasonLine content={props.content} reason={reason} lead="Cannot dismiss:" />
    </ScreenDialog>
  );
}

/** Dismissing a hero takes his army with him and he is gone for the game. */
export function DismissHeroDialog(props: {
  readonly content: ContentRegistry;
  readonly hero: HeroState;
  readonly army: ArmyState | null;
  readonly checkCommand?: ((cmd: GameCommand) => Reason | null) | undefined;
  readonly onDismiss: (cmd: GameCommand) => void;
  readonly onClose: () => void;
}): JSX.Element {
  const def = props.content.heroes.get(props.hero.def) ?? null;
  const cmd: GameCommand = { type: "DISMISS_HERO", hero: props.hero.id };
  const reason = props.checkCommand?.(cmd) ?? null;
  const carried = (props.army?.slots ?? []).filter((s) => s !== null).length;

  return (
    <ScreenDialog
      title={`Dismiss ${def?.name ?? "this hero"}?`}
      onClose={props.onClose}
      width={520}
      footer={
        <button type="button" className="hs-btn hs-btn--gold" disabled={reason !== null}
          title={reason === null ? "Send him away" : explainReason(props.content, reason)}
          onClick={() => { props.onDismiss(cmd); }}>
          Dismiss him
        </button>
      }
    >
      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
        <div className="hs-portrait">
          <MaybeIcon atlasKey={def === null ? null : heroPortrait(def.portrait)}
            size={PORTRAIT_SMALL} title={def?.name ?? "Hero"} />
        </div>
        <div className="hs-card hs-stone" style={{ flex: "1 1 auto", minWidth: 0 }}>
          <div className="hs-card__title">{def?.name ?? "Hero"}</div>
          <StatLine label="Level" value={String(props.hero.level)} />
          <StatLine label="Stacks he is carrying" value={String(carried)} />
          <div className="hs-tiny" style={{ lineHeight: 1.45, marginTop: "6px" }}>
            He leaves for good, and everything he is carrying goes with him. Move
            his troops into the garrison first if you want to keep them.
          </div>
        </div>
      </div>
      <ReasonLine content={props.content} reason={reason} lead="Cannot dismiss:" />
    </ScreenDialog>
  );
}
